/**
 * Source-of-truth for field references the chart depends on. A discrepancy
 * between the chart's references and the new dataset's available fields is
 * what triggers the "missing fields" warning in the Review versions flow.
 *
 * Returns the raw references as they appear in the chart (e.g.
 * `auto_dataset_123.c_status`). The cube/short-name prefix is preserved so
 * callers can report exactly what the chart had — the diff itself compares
 * only the field name (text after the last `.`).
 */
export const collectChartFieldReferences = (chart) => {
    if (!chart?.data) {
        return [];
    }
    const refs = new Set();
    const add = (key) => {
        if (key) {
            refs.add(key);
        }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const query = chart.data.query;
    for (const m of (query?.measures || [])) {
        add(m);
    }
    for (const d of (query?.dimensions || [])) {
        add(d);
    }
    for (const td of (query?.timeDimensions || [])) {
        add(td?.dimension);
    }
    for (const f of (query?.filters || [])) {
        add(f?.member);
    }
    // Settings-panel selections are also field references. A user may have a
    // column / axis selection that hasn't been baked into the executed query
    // yet (e.g. the chart hasn't been Run on the new version) — those still
    // need to be considered when warning about missing fields.
    const { data } = chart;
    add(data.xAxis?.key);
    add(data.yAxis?.key);
    add(data.splitBy?.key);
    add(data.dotSizeField?.key);
    for (const rule of data.defaultSort || []) {
        add(rule?.key);
    }
    for (const m of (data.additionalMeasures || [])) {
        add(m?.key);
    }
    for (const c of (data.columns || [])) {
        add(c?.key);
    }
    return Array.from(refs);
};
/**
 * Returns the set of field names defined by the dataset (CUBE_CONFIG)
 * resource's `data.dimensions` and `data.measures` objects. Names are
 * compared on the suffix after the cube prefix, since the cube short-name
 * is also stored on the dataset and the chart's references include it as a
 * prefix.
 */
export const collectDatasetFieldNames = (dataset) => {
    const names = new Set();
    for (const key of Object.keys(dataset?.data?.dimensions || {})) {
        names.add(key);
    }
    for (const key of Object.keys(dataset?.data?.measures || {})) {
        names.add(key);
    }
    return names;
};
const extractFieldName = (ref) => {
    const idx = ref.lastIndexOf('.');
    return idx >= 0 ? ref.slice(idx + 1) : ref;
};
/**
 * Returns the chart references that have no matching field name in the new
 * dataset's available field set. Preserves the original reference form for
 * display ("auto_dataset_123.removed_field" stays intact in the warning).
 */
export const findMissingFields = (fieldReferences, availableFieldNames) => fieldReferences.filter((ref) => !availableFieldNames.has(extractFieldName(ref)));
const isKeyAvailable = (key, availableFieldNames) => Boolean(key) && availableFieldNames.has(extractFieldName(key));
const scrubColumn = (col, availableFieldNames) => col && isKeyAvailable(col.key, availableFieldNames) ? col : undefined;
const scrubColumns = (cols, availableFieldNames) => (cols ? cols.filter((c) => isKeyAvailable(c?.key, availableFieldNames)) : cols);
/**
 * SSP-3464: when a chart's dataset is upgraded to a new version that no
 * longer exposes some of the fields the chart was built on, strip those
 * dead references from the chart's settings so the dimensions / measures
 * sidebar doesn't show ghost selections and the executed query doesn't
 * fail.
 *
 * Returns a new ChartData object with dead references removed. Single-value
 * axis fields (xAxis / yAxis / splitBy / dotSizeField) become `undefined`
 * when invalid; array-typed selections are filtered. Display-only metadata
 * (dimensionOrder, measureOrder, conditionalFormats) is left untouched as
 * those track display names, not field keys.
 */
export const scrubChartDataFieldReferences = (data, availableFieldNames) => {
    if (!data) {
        return data;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const query = data.query;
    const scrubbedQuery = query
        ? {
            ...query,
            ...(Array.isArray(query.measures)
                ? { measures: query.measures.filter((m) => isKeyAvailable(m, availableFieldNames)) }
                : {}),
            ...(Array.isArray(query.dimensions)
                ? { dimensions: query.dimensions.filter((d) => isKeyAvailable(d, availableFieldNames)) }
                : {}),
            ...(Array.isArray(query.timeDimensions)
                ? {
                    timeDimensions: query.timeDimensions.filter((td) => isKeyAvailable(td?.dimension, availableFieldNames)),
                }
                : {}),
            ...(Array.isArray(query.filters)
                ? {
                    filters: query.filters.filter((f) => isKeyAvailable(f?.member, availableFieldNames)),
                }
                : {}),
            ...(query.order && typeof query.order === 'object'
                ? {
                    order: Object.fromEntries(Object.entries(query.order).filter(([k]) => isKeyAvailable(k, availableFieldNames))),
                }
                : {}),
        }
        : query;
    return {
        ...data,
        query: scrubbedQuery,
        xAxis: scrubColumn(data.xAxis, availableFieldNames),
        yAxis: scrubColumn(data.yAxis, availableFieldNames),
        splitBy: scrubColumn(data.splitBy, availableFieldNames),
        dotSizeField: scrubColumn(data.dotSizeField, availableFieldNames),
        additionalMeasures: scrubColumns(data.additionalMeasures, availableFieldNames),
        columns: scrubColumns(data.columns, availableFieldNames),
        filters: data.filters?.filter((f) => isKeyAvailable(f?.member, availableFieldNames)),
        orderRules: data.orderRules?.filter((o) => isKeyAvailable(o?.key, availableFieldNames)),
        defaultSort: data.defaultSort?.filter((r) => isKeyAvailable(r?.key, availableFieldNames)),
        numberValues: data.numberValues?.filter((n) => isKeyAvailable(n?.value?.key, availableFieldNames)),
    };
};
